import React, { useState, useEffect } from 'react';
import CustomerServicePopup from './CustomerServicePopup';
import { useAdminAuth } from '../hooks/useAdminAuth';

function MyPage({ onBack, onDepositClick, onWithdrawClick, onPersonalInfoClick, onPaymentMethodsClick, onNotificationClick, onLogout, userProfile }) {
  const { isAdmin, loading: adminLoading } = useAdminAuth();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [showCustomerServicePopup, setShowCustomerServicePopup] = useState(false);
  
  // Prevent admin users from viewing regular user profile
  if (!adminLoading && isAdmin) {
    return (
      <div className="my-page">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh', flexDirection: 'column' }}>
          <div className="alert alert-warning" style={{ maxWidth: '500px', margin: '20px' }}>
            <h4 className="alert-heading">
              <i className="bi bi-shield-exclamation me-2"></i>
              Admin Account
            </h4>
            <p>Admin accounts should access the system through the admin panel.</p>
            <hr />
            <button className="btn btn-primary" onClick={onBack}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Use userProfile directly from props (already loaded in App.js)
  // Show default values if profile is still loading (no blocking spinner)
  const username = userProfile?.username || 'User';
  const invitationCode = userProfile?.invitationCode || 'Loading...';
  const creditScore = userProfile?.creditScore ?? 100;
  const walletAmount = userProfile?.balances?.wallet || 0;
  const frozenBalance = userProfile?.balances?.frozen || 0;
  const commission = userProfile?.balances?.commission || 0;
  const vipLevel = userProfile?.vipLevel || 'VIP1';
  const vipProgress = 100; // Can be calculated based on VIP requirements

  const displayWalletAmount = walletAmount;
  const displayFrozenAmount = frozenBalance;
  const displayTotalAmount = walletAmount + commission;
  
  // For user display: Show normal balances in stealth mode
  const walletValue = displayWalletAmount.toFixed(2); // Wallet Amount (shows original total in stealth mode)
  const walletCurrency = 'USDT';
  const commissionValue = commission.toFixed(2); // Commission shows total commission earned
  const commissionCurrency = 'USDT';
  const frozenValue = displayFrozenAmount.toFixed(2); // Frozen Balance (always 0 in stealth mode)
  const totalValue = displayTotalAmount.toFixed(2); // Total Balance (shows original total in stealth mode)

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  const handleCopyInvitationCode = () => {
    if (invitationCode && invitationCode !== 'Loading...') {
      navigator.clipboard.writeText(invitationCode).then(() => {
        alert('Invitation code copied to clipboard!');
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = invitationCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Invitation code copied to clipboard!');
      });
    }
  };

  return (
    <div className="my-page">
      {/* Top Navigation Bar */}
      <div className="my-page-top-bar">
        <div className="my-page-back-btn" onClick={onBack}>
          <i className="bi bi-chevron-left"></i>
        </div>
        <div className="my-page-title-text">Profile</div>
        <div className="my-page-spacer"></div>
      </div>

      {/* Profile Hero */}
      <div className="my-page-hero-section">
        <div className="dashboard-hero-card my-page-hero-card">
          <div className="dashboard-hero-left">
            <div className="dashboard-hero-right mobile-vip-badge">
              <div className="dashboard-hero-vip">
                <div className="dashboard-hero-vip-icon">
                  <img 
                    src={`/levels/level-${vipLevel === 'VIP4' ? '4' : vipLevel === 'VIP3' ? '3' : vipLevel === 'VIP2' ? '2' : '1'}.png`} 
                    alt={vipLevel} 
                  />
                </div>
                <div className="dashboard-hero-vip-info">
                  <div className="dashboard-hero-vip-level">{vipLevel}</div>
                </div>
              </div>
            </div>
            <div className="dashboard-hero-profile">
              <div className="dashboard-hero-avatar">
                <i className="bi bi-person-circle"></i>
              </div>
              <div className="dashboard-hero-details">
                <div className="dashboard-hero-username">{username}</div>
                <div className="dashboard-hero-invite">
                  Invitation Code: <span 
                    className="dashboard-hero-invite-code" 
                    onClick={handleCopyInvitationCode}
                    style={{ cursor: 'pointer', userSelect: 'all' }}
                    title="Click to copy"
                  >
                    {invitationCode}
                  </span>
                  <i 
                    className="bi bi-clipboard ms-2" 
                    onClick={handleCopyInvitationCode}
                    style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                    title="Copy invitation code"
                  ></i>
                </div>
              </div>
            </div>

            <div className="dashboard-hero-credit">
              <div className="dashboard-hero-credit-header">
                <div className="dashboard-hero-credit-label">Credit Score:</div>
                <div className="dashboard-hero-credit-value">{creditScore}%</div>
              </div>
              <div className="dashboard-hero-progress">
                <div className="dashboard-hero-progress-bar" style={{ width: `${creditScore}%` }} />
              </div>
            </div>

            <div className="dashboard-hero-stats">
              <div className="dashboard-hero-stat">
                <div className="dashboard-hero-stat-label">Wallet Amount</div>
                <div className="dashboard-hero-stat-value">
                  {walletValue} <span>{walletCurrency}</span>
                </div>
              </div>
              <div className="dashboard-hero-divider" />
              <div className="dashboard-hero-stat">
                <div className="dashboard-hero-stat-label">Commission</div>
                <div className="dashboard-hero-stat-value">
                  {commissionValue} <span>{commissionCurrency}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-hero-right desktop-vip-badge">
            <div className="dashboard-hero-vip">
              <div className="dashboard-hero-vip-icon">
                <img 
                  src={`/levels/level-${vipLevel === 'VIP4' ? '4' : vipLevel === 'VIP3' ? '3' : vipLevel === 'VIP2' ? '2' : '1'}.png`} 
                  alt={vipLevel} 
                />
              </div>
              <div className="dashboard-hero-vip-info">
                <div className="dashboard-hero-vip-level">{vipLevel}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="my-page-content">
        {/* My Financial Section */}
        <div className="my-page-section">
          <div className="my-page-section-title">My Financial</div>
          <div className="my-page-list">
            <div className="my-page-list-item" onClick={onDepositClick}>
              <div className="my-page-list-icon">
                <i className="bi bi-credit-card"></i>
              </div>
              <div className="my-page-list-label">Deposit</div>
              <div className="my-page-list-arrow">
                <i className="bi bi-chevron-right"></i>
              </div>
            </div>
            <div className="my-page-list-item" onClick={onWithdrawClick}>
              <div className="my-page-list-icon">
                <i className="bi bi-shop"></i>
              </div>
              <div className="my-page-list-label">Withdraw</div>
              <div className="my-page-list-arrow">
                <i className="bi bi-chevron-right"></i>
              </div>
            </div>
          </div>
        </div>

        {/* My Detail Section */}
        <div className="my-page-section">
          <div className="my-page-section-title">My Detail</div>
          <div className="my-page-list">
            <div 
              className="my-page-list-item"
              onClick={onPersonalInfoClick}
            >
              <div className="my-page-list-icon">
                <i className="bi bi-pencil"></i>
              </div>
              <div className="my-page-list-label">Personal Information</div>
              <div className="my-page-list-arrow">
                <i className="bi bi-chevron-right"></i>
              </div>
            </div>
            <div 
              className="my-page-list-item"
              onClick={onPaymentMethodsClick}
            >
              <div className="my-page-list-icon">
                <i className="bi bi-wallet2"></i>
              </div>
              <div className="my-page-list-label">Payment Methods</div>
              <div className="my-page-list-arrow">
                <i className="bi bi-chevron-right"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Other Section */}
        <div className="my-page-section">
          <div className="my-page-section-title">Other</div>
          <div className="my-page-list">
            <div 
              className="my-page-list-item"
              onClick={() => setShowCustomerServicePopup(true)}
            >
              <div className="my-page-list-icon">
                <i className="bi bi-headset"></i>
              </div>
              <div className="my-page-list-label">Contact Us</div>
              <div className="my-page-list-arrow">
                <i className="bi bi-chevron-right"></i>
              </div>
            </div>
            <div 
              className="my-page-list-item"
              onClick={onNotificationClick}
            >
              <div className="my-page-list-icon">
                <i className="bi bi-bell"></i>
              </div>
              <div className="my-page-list-label">Notifications</div>
              <div className="my-page-list-arrow">
                <i className="bi bi-chevron-right"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <div className="my-page-logout-container">
          <button className="my-page-logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      {/* Customer Service Popup */}
      {showCustomerServicePopup && (
        <CustomerServicePopup onClose={() => setShowCustomerServicePopup(false)} />
      )}
    </div>
  );
}

export default MyPage;

